import request from 'services/axios'

import { isMiniapp, resolvePlatform } from 'modules/miniapp/detect'

/**
 * Where this browser is: the web version, or Hamyan as a mini app inside a
 * messenger. Sent with every question so the answer can suit the place.
 *
 * WHY THE SERVER NEEDS IT. Several of the knowledge base's commonest
 * sentences are wrong in one environment or the other. «برنامه را نصب کنید»
 * to somebody who opened Hamyan inside Eitaa tells a person already using it
 * that they are not; «همین‌جا در ایتا به پشتیبانی پیام بدهید» is one tap
 * inside Eitaa and an app switch inside Bale. The server cannot tell from the
 * request — a mini-app webview is just a browser — so the client says.
 *
 * `undefined` when we genuinely do not know, and that is a real answer: the
 * server treats a missing surface as «unknown» and adds nothing, rather than
 * assuming the web. Never guessed: `resolvePlatform()` returns null in an
 * ordinary tab, and `isMiniapp()` is what separates "an ordinary tab" from
 * "a mini app whose messenger we failed to name".
 */
export const currentSurface = (): string | undefined => {
    const platform = resolvePlatform()
    if (platform) return platform
    return isMiniapp() ? undefined : 'web'
}

export interface ISupportBotTurn {
    role: 'user' | 'assistant'
    content: string
}

export interface ISupportBotAnswer {
    answer: string
    /** True when the backend routed this to a human before asking the model. */
    escalated: boolean
    tools_used: string[]
    /**
     * How the answer was actually produced — the server owns the vocabulary.
     *
     * Optional because a canned failure reply comes back as a perfectly normal
     * 200, so every deploy of this client can meet a backend that does not send
     * these yet. Read them defensively; never assume `outcome` is a success.
     */
    outcome?: string
    escalation_reason?: string
    /**
     * The thread this exchange joined. Send it back on the next question, and
     * it is what `getSupportBotConversation` restores.
     *
     * Optional for the same reason as `outcome`: a backend without threading
     * omits it, and the chat must still work — it just starts fresh each time,
     * which is what it did before.
     */
    conversation_id?: number | null
    /**
     * Whether a person was genuinely told to call this member back.
     *
     * NOT cosmetic. The server only sets it when the team group was really
     * notified, and the answer text promises a callback only in that case. The
     * UI must not offer «با شما تماس می‌گیریم» on its own inference: the bot
     * once promised exactly that with nothing behind it, and the member waited.
     */
    human_notified?: boolean
}

export interface ISupportBotConversation {
    conversation_id: number
    turns: ISupportBotTurn[]
    last_message_at: string | null
}

/**
 * پشتیبان هوشمند — POST a question, get an answer.
 *
 * The server decides everything that matters: which fund data the answer may
 * draw on (always scoped to the JWT, never to anything sent from here) and
 * whether the message should skip the model and go straight to a human.
 *
 * `history` is now a fallback rather than the source of truth — when a
 * `conversation_id` is in play the server replays the thread it wrote itself,
 * which is both unforgeable and survives this component unmounting.
 */
export const askSupportBot = async (payload: {
    question: string
    history?: ISupportBotTurn[]
    conversationId?: number | null
}) => {
    const surface = currentSurface()
    const { data }: { data: ISupportBotAnswer } = await request.post(
        '/api/v1/support-bot/ask/',
        {
            question: payload.question,
            history: payload.history ?? [],
            ...(payload.conversationId
                ? { conversation_id: payload.conversationId }
                : {}),
            ...(surface ? { surface } : {}),
        }
    )
    return data
}

/**
 * The caller's still-open chat thread, or null.
 *
 * WHY THIS EXISTS. The bot now answers «چطور قسطم را بدهم؟» with a link to the
 * loan instead of a description of where to tap. That is the fix for its worst
 * habit — inventing screens that do not exist — but it means a good answer ends
 * with the member leaving this modal, and the modal keeps its turns in
 * component state. The better the answer, the more certainly it destroyed the
 * conversation. This puts them back where they were.
 *
 * Scoped entirely to the authenticated user; there is no id to pass and
 * therefore no id to get wrong.
 */
export const getSupportBotConversation = async () => {
    const { data }: { data: { conversation: ISupportBotConversation | null } } =
        await request.get('/api/v1/support-bot/conversation/')
    return data.conversation
}

export interface ISupportBotGuestAnswer {
    answer: string
    outcome: string | null
    topic: string | null
    rounds: number
    provider_ms: number
    guest: true
}

/**
 * The same bot, for somebody with no account — the قرعه‌کشی landing.
 *
 * A separate endpoint rather than a mode on `askSupportBot`, mirroring the
 * server: `ask` derives everything it may read from the caller's JWT, and this
 * caller has none. So there is no `conversation_id` here and no thread to
 * restore — the whole history lives in this browser and is replayed each time,
 * which the server treats as untrusted and replays only as plain turns.
 */
export const askSupportBotAsGuest = async (payload: {
    question: string
    history?: ISupportBotTurn[]
}) => {
    const { data }: { data: ISupportBotGuestAnswer } = await request.post(
        '/api/v1/support-bot/guest/ask/',
        {
            question: payload.question,
            history: payload.history ?? [],
        }
    )
    return data
}
